import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Home, KeyRound, LayoutGrid, LogOut, Menu, PanelLeft } from 'lucide-react';
import type { LiveStatus } from '../../hooks/useLiveUpdates';
import { t, tr } from '../../i18n';
import { useAuthStore, useUIStore, useViewModeStore } from '../../store';
import { cn } from '../../utils/helpers';
import { BrandMark } from './Brand';
import { LanguageSwitcher } from './LanguageSwitcher';

const LIVE_LABELS: Record<LiveStatus, string> = tr({
  connecting: 'Pripájanie…',
  online: 'Živé aktualizácie zapnuté',
  offline: 'Živé aktualizácie nedostupné – údaje sa obnovia pri ďalšej akcii',
});

export function Header({ liveStatus, onLogout }: { liveStatus: LiveStatus; onLogout: () => void }) {
  const user = useAuthStore((s) => s.user);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const { viewMode, setViewMode } = useViewModeStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const initials = (user?.full_name || user?.username || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-secondary-200 bg-white/95 px-4 backdrop-blur lg:px-6">
      {viewMode === 'tiles' ? (
        <Link to="/" className="flex min-w-0 items-center gap-3 rounded-lg hover:opacity-80" title={t('Domov')}>
          <Home className="h-5 w-5 shrink-0 text-secondary-500" aria-hidden="true" />
          <span className="hidden sm:block">
            <BrandMark />
          </span>
          <span className="sr-only">{t('Domov')}</span>
        </Link>
      ) : (
        <button className="icon-btn lg:hidden" onClick={() => setSidebarOpen(true)} aria-label={t('Otvoriť menu')}>
          <Menu className="h-6 w-6" />
        </button>
      )}
      <div className="flex-1" />

      <div className="flex items-center gap-3">
        <span className="flex items-center gap-2 text-xs text-secondary-500" title={LIVE_LABELS[liveStatus]}>
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              liveStatus === 'online' ? 'bg-green-500' : liveStatus === 'offline' ? 'bg-secondary-300' : 'bg-amber-400',
            )}
          />
          <span className="hidden sm:inline">{liveStatus === 'online' ? 'Online' : liveStatus === 'offline' ? 'Offline' : '…'}</span>
        </span>

        <button
          className="icon-btn"
          onClick={() => {
            setViewMode(viewMode === 'tiles' ? 'standard' : 'tiles');
            navigate('/');
          }}
          title={viewMode === 'tiles' ? t('Prepnúť na štandardné zobrazenie') : t('Prepnúť na zobrazenie s dlaždicami')}
          aria-label={viewMode === 'tiles' ? t('Prepnúť na štandardné zobrazenie') : t('Prepnúť na zobrazenie s dlaždicami')}
        >
          {viewMode === 'tiles' ? <PanelLeft className="h-5 w-5" /> : <LayoutGrid className="h-5 w-5" />}
        </button>

        <LanguageSwitcher />

        <div className="relative">
          <button
            onClick={() => setMenuOpen((open) => !open)}
            className="flex items-center gap-2 rounded-lg p-1.5 text-secondary-700 hover:bg-secondary-100"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
              {initials}
            </span>
            <span className="hidden text-left md:block">
              <span className="block text-sm font-medium leading-tight">{user?.full_name || user?.username}</span>
              <span className="block text-xs leading-tight text-secondary-500">{user?.role === 'admin' ? t('Administrátor') : t('Používateľ')}</span>
            </span>
            <ChevronDown className="hidden h-4 w-4 text-secondary-400 md:block" />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden="true" />
              <div role="menu" className="absolute right-0 z-50 mt-2 w-52 rounded-lg border border-secondary-200 bg-white py-1 shadow-lg">
                <Link
                  to="/account"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                >
                  <KeyRound className="h-4 w-4" /> {t('Môj účet a heslo')}
                </Link>
                <button
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout();
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" /> {t('Odhlásiť sa')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
